import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import i18next from 'i18next';
import { MESSAGE_TYPES, ReceiveMessageType } from 'src/@types/message';
import useWebViewCommunication from 'src/hooks/useWebViewCommunication';
import { useStores } from 'src/models';
import { PATH_PAGE } from 'src/routes/paths';
import { settingsApi } from 'src/services';
import { isNativeApp } from 'src/utils/environment';

// App에서 전달하는 NAVIGATE target을 웹 라우트로 매핑
const NAVIGATE_TARGET_MAP: Record<string, string> = {
  mission: PATH_PAGE.mission.root,
  missionHistory: PATH_PAGE.mission.history,
  nicojini: PATH_PAGE.drJin.root,
  diary: PATH_PAGE.calendar.root,
  craving: PATH_PAGE.craving.root,
};

function AppMessageListener() {
  const { authStore } = useStores();
  const navigate = useNavigate();

  // RN 메시지 핸들러
  const handleRNMessage = useCallback(
    (message: ReceiveMessageType) => {
      // 서버 응답 메시지 무시
      if ('resultCode' in message) {
        return;
      }

      switch (message.type) {
        case MESSAGE_TYPES.DEVICE_TOKEN: {
          const token = message.payload.deviceToken;
          settingsApi.saveDeviceToken(token);
          break;
        }
        case MESSAGE_TYPES.NAVIGATE: {
          const target = message.payload.target;
          const route = NAVIGATE_TARGET_MAP[target];
          if (route) {
            navigate(route);
          } else {
            console.warn(`[AppMessageListener] 알 수 없는 NAVIGATE target: ${target}`);
          }
          break;
        }
      }
    },
    [navigate],
  );

  const { sendMessage } = useWebViewCommunication(handleRNMessage);

  // 디바이스 토큰 요청 (기존 로직 보존)
  useEffect(() => {
    if (authStore.user) {
      if (!authStore.isSaved) {
        sendMessage({ type: MESSAGE_TYPES.DEVICE_TOKEN });
        authStore.setProp('isSaved', true);
      }
    }
  }, [sendMessage, authStore]);

  // LANGUAGE_CHANGE 메시지 전송 (Web -> App)
  // 마운트 시 현재 언어 전송 + 언어 변경 이벤트 리스닝
  useEffect(() => {
    if (!isNativeApp()) return;

    // 마운트 시 현재 언어 전송
    sendMessage({
      type: MESSAGE_TYPES.LANGUAGE_CHANGE,
      payload: { language: i18next.language || 'ko' },
    });

    // 언어 변경 시 App에 알림
    const handleLanguageChanged = (lng: string) => {
      sendMessage({
        type: MESSAGE_TYPES.LANGUAGE_CHANGE,
        payload: { language: lng },
      });
    };

    i18next.on('languageChanged', handleLanguageChanged);

    return () => {
      i18next.off('languageChanged', handleLanguageChanged);
    };
  }, [sendMessage]);

  return null;
}

export default AppMessageListener;
